import api from "./apiService";

/**
 * Check if the browser supports push notifications
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Get the VAPID public key from the backend
 */
async function getVapidPublicKey() {
  const response = await api.get('/push/vapid-public-key');
  return response.data.publicKey;
}

/**
 * Convert a base64 URL-safe string to a Uint8Array (needed for applicationServerKey)
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Subscribe the user to push notifications.
 * Requests browser permission, creates a PushSubscription, and sends it to the backend.
 *
 * @returns {Promise<boolean>} true if subscribed successfully
 */
export async function subscribeToPush() {
  if (!isPushSupported()) return false;

  // Service workers require localhost or HTTPS — wait with a timeout
  let registration;
  try {
    registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Service worker not ready — push notifications require HTTPS or localhost')), 5000)
      )
    ]);
  } catch (err) {
    console.warn('Push subscription failed:', err.message);
    return false;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const vapidPublicKey = await getVapidPublicKey();

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
  });

  const subscriptionJSON = subscription.toJSON();
  await api.post('/push/subscribe', {
    endpoint: subscriptionJSON.endpoint,
    keys: subscriptionJSON.keys
  });

  return true;
}

/**
 * Unsubscribe the user from push notifications.
 *
 * @returns {Promise<boolean>} true if unsubscribed successfully
 */
export async function unsubscribeFromPush() {
  if (!isPushSupported()) return false;

  let registration;
  try {
    registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) => setTimeout(() => reject(new Error('SW not ready')), 3000))
    ]);
  } catch { return false; }

  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    await api.post('/push/unsubscribe', { endpoint });
  }

  return true;
}

/**
 * Check if the user currently has an active push subscription in this browser.
 *
 * @returns {Promise<boolean>}
 */
export async function isPushSubscribed() {
  if (!isPushSupported()) return false;

  let registration;
  try {
    registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) => setTimeout(() => reject(new Error('SW not ready')), 3000))
    ]);
  } catch { return false; }

  const subscription = await registration.pushManager.getSubscription();
  return !!subscription;
}

/**
 * Get push subscription status from the backend (across all browsers/devices).
 *
 * @returns {Promise<{subscribed: boolean, subscription_count: number}>}
 */
export async function getPushStatus() {
  const response = await api.get('/push/status');
  return response.data;
}

const pushService = {
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
  isPushSubscribed,
  getPushStatus
};

export default pushService;
